import { useAuth } from "@/contexts/AuthContext";
import type { ChatMessage } from "@/services/caseChatServiceV2.types";
import { streamChatMessage } from "@/services/caseChatStream";
import { useCallback, useEffect, useRef, useState } from "react";

import { useCaseChatService } from "./useCaseChatService";
import { invalidateCaseChatsCache } from "./useCaseChats";

const conversationStorageKey = (caseId: string) => `case-chat-conversation-${caseId}`;

/** Clears the resumable conversation so the panel's next mount starts blank. */
export const clearStoredConversation = (caseId: string) => {
  sessionStorage.removeItem(conversationStorageKey(caseId));
};

export const useCaseChat = (
  caseId: string,
  selectedConversationId?: string | null,
  // Bumped to force a reset even when selectedConversationId is unchanged (e.g. repeated "New chat").
  resetToken?: number
) => {
  const service = useCaseChatService();
  const { getAccessToken } = useAuth();
  const [conversationId, setConversationId] = useState<string | null>(() =>
    // Explicit null means "start blank"; only undefined falls back to sessionStorage.
    selectedConversationId !== undefined
      ? selectedConversationId
      : sessionStorage.getItem(conversationStorageKey(caseId))
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [title, setTitle] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const skipNextHistoryLoadRef = useRef(false);

  // Last (conversationId, resetToken) adopted; ignores conversationId drift from sendMessage.
  const lastAdoptedRef = useRef({ conversationId: selectedConversationId ?? null, resetToken });

  // Adopts an externally selected conversation, or null for an explicit "New chat".
  useEffect(() => {
    const normalized = selectedConversationId ?? null;
    const last = lastAdoptedRef.current;
    if (normalized === last.conversationId && resetToken === last.resetToken) return;
    lastAdoptedRef.current = { conversationId: normalized, resetToken };
    abortControllerRef.current?.abort();
    setIsStreaming(false);
    setConversationId(normalized);
    setMessages([]);
    setTitle(null);
    setError(null);
    if (normalized) {
      sessionStorage.setItem(conversationStorageKey(caseId), normalized);
    } else {
      sessionStorage.removeItem(conversationStorageKey(caseId));
    }
  }, [selectedConversationId, resetToken, caseId]);

  useEffect(() => {
    if (!conversationId) return;
    if (skipNextHistoryLoadRef.current) {
      skipNextHistoryLoadRef.current = false;
      return;
    }
    let cancelled = false;
    setIsLoadingHistory(true);
    service
      .getConversation(caseId, conversationId)
      .then((data) => {
        if (!cancelled) {
          setMessages(data.messages);
          setTitle(data.conversation.title);
        }
      })
      .catch(() => {
        if (!cancelled) {
          sessionStorage.removeItem(conversationStorageKey(caseId));
          setConversationId(null);
          setError(
            "Couldn't load that conversation. It may have been removed, or there was a network issue."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, conversationId, service]);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const sendMessage = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || isStreaming) return;

      setError(null);
      setIsStreaming(true);
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      try {
        let activeConversationId = conversationId;
        if (!activeConversationId) {
          const conversation = await service.createConversation(caseId);
          activeConversationId = conversation.conversation_id;
          skipNextHistoryLoadRef.current = true;
          setConversationId(activeConversationId);
          sessionStorage.setItem(conversationStorageKey(caseId), activeConversationId);
          invalidateCaseChatsCache(caseId);
        }

        const now = new Date().toISOString();
        const userMessageId = `local-user-${crypto.randomUUID()}`;
        const assistantMessageId = `local-assistant-${crypto.randomUUID()}`;
        setMessages((prev) => [
          ...prev,
          {
            message_id: userMessageId,
            conversation_id: activeConversationId!,
            role: "user",
            content: trimmed,
            created_at: now,
          },
          {
            message_id: assistantMessageId,
            conversation_id: activeConversationId!,
            role: "assistant",
            content: "",
            created_at: now,
          },
        ]);

        await streamChatMessage({
          caseId,
          conversationId: activeConversationId,
          question: trimmed,
          getAccessToken,
          signal: abortController.signal,
          onDelta: (delta) => {
            setMessages((prev) =>
              prev.map((message) =>
                message.message_id === assistantMessageId
                  ? { ...message, content: message.content + delta }
                  : message
              )
            );
          },
          onTitle: (newTitle) => {
            setTitle(newTitle);
            invalidateCaseChatsCache(caseId);
          },
        });
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setError("Something went wrong generating a response. Please try again.");
        }
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
    [caseId, conversationId, isStreaming, service, getAccessToken]
  );

  const startNewConversation = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsStreaming(false);
    setConversationId(null);
    setMessages([]);
    setTitle(null);
    setError(null);
    sessionStorage.removeItem(conversationStorageKey(caseId));
  }, [caseId]);

  return {
    messages,
    title,
    sendMessage,
    isStreaming,
    isLoadingHistory,
    error,
    startNewConversation,
  };
};
